import React, { Fragment, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableCheckboxCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderCheckboxCell,
    DataTableHeaderRow,
    DataTableOverflowMenuCell,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    GridCell,
    GridRow,
    List,
    ListItem,
    TextButton,
    TextField,
    Typography,
} from "@episerver/ui-framework";
import { ContactNote } from "../model/ContactNote";
import {
    selectedContactNotesSelector,
    contactNoteSelector,
    paginationSelector,
    onChangeContactNotePage,
    onChangeContactNoteRowPerPage,
    dialogStatusSelector,
    onChangeDialogStatus,
    confirmDialogStatusSelector,
    onChangeConfirmDialogStatus,
    selectAllContactNotes,
    deselectAllContactNotes,
    selectContactNote,
    deselectContactNote,
} from "../reducers/contactNoteManagerReducer";
import {
    contactSelector,
    onAddOrUpdateContactNote,
    onDeleteContactNote,
} from "../../ContactManager/reducers/contactDetailReducer";
import { Controller, useForm } from "react-hook-form";
import ExtendedProperty from "../../ExtendedProperty/ExtendedProperty";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";
import { fetchContactNoteById } from "../reducers/asyncThunks";

const ContactNoteManager = () => {
    const dispatch = useAppDispatch();
    const contact = useAppSelector(contactSelector);
    const selectedContactNotes = useAppSelector(selectedContactNotesSelector);
    const contactNote = useAppSelector(contactNoteSelector);
    const dialogStatus = useAppSelector(dialogStatusSelector);
    const confirmDialogStatus = useAppSelector(confirmDialogStatusSelector);
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useAppSelector(paginationSelector);

    const columns: Array<{ selector: string; title: string; sortable: boolean }> = [
        { selector: "noteTitle", title: "Title", sortable: false },
        { selector: "noteContent", title: "Note Text", sortable: false },
        { selector: "contactId", title: "Originated By", sortable: false },
        { selector: "created", title: "Created", sortable: true },
        { selector: "", title: "", sortable: false },
    ];

    const {
        control,
        watch,
        setValue,
        setError,
        clearErrors,
        trigger,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<ContactNote>({
        reValidateMode: "onChange",
        mode: "onChange",
        defaultValues: contactNote,
    });

    const values = watch();

    const initComponent = async () => {
        if (!contactNote.isFetched) {
            dispatch(fetchContactNoteById(contactNote.contactNoteId ?? ""));
        }
    };

    useEffect(() => {
        if (dialogStatus) {
            initComponent();
        }
    }, [contactNote?.contactNoteId, dialogStatus]);

    useEffect(() => {
        reset(contactNote);
    }, [contactNote]);

    const onToggleAllSelected = (checked: boolean): void => {
        if (checked) {
            dispatch(selectAllContactNotes(contact.contactNotes));
        } else {
            dispatch(deselectAllContactNotes());
        }
    };

    const isSelected = (row: ContactNote): boolean => {
        return selectedContactNotes.includes(row.contactNoteId!);
    };

    const onSelectedRowChanged = (row: ContactNote, checked: boolean): void => {
        if (checked) {
            dispatch(selectContactNote(row.contactNoteId));
        } else {
            dispatch(deselectContactNote(row.contactNoteId));
        }
    };

    const renderRow = (rowData: ContactNote, idx: number): React.ReactElement => {
        let created = rowData.created != null ? new Date(rowData.created.replace("Z", "")).toLocaleString() : "";

        return (
            <DataTableRow rowId={rowData.contactNoteId!} key={rowData.contactNoteId} selected={isSelected(rowData)}>
                <DataTableCheckboxCell
                    defaultChecked={isSelected(rowData)}
                    onChange={(e) => onSelectedRowChanged(rowData, e.target.checked)}
                />
                <DataTableCell>{rowData.noteTitle}</DataTableCell>
                <DataTableCell>{rowData.noteContent}</DataTableCell>
                <DataTableCell>{rowData.contactName ?? ""}</DataTableCell>
                <DataTableCell>{created}</DataTableCell>
                <DataTableOverflowMenuCell>
                    <List>
                        <ListItem
                            onClick={() => {
                                dispatch(
                                    onChangeDialogStatus({
                                        dialogStatus: true,
                                        contactNote: {...rowData, idx, isNew: false},
                                    })
                                );
                            }}
                        >
                            Edit
                        </ListItem>
                        <ListItem
                            onClick={() =>
                                dispatch(
                                    onChangeConfirmDialogStatus({
                                        dialogOpen: true,
                                        contactNoteIds: [rowData.contactNoteId],
                                    })
                                )
                            }
                        >
                            Delete
                        </ListItem>
                    </List>
                </DataTableOverflowMenuCell>
            </DataTableRow>
        );
    };

    return (
        <Fragment>
            <DataTable>
                <DataTableToolbar>
                    <Fragment>
                        <TextButton
                            onClick={() => {
                                dispatch(
                                    onChangeDialogStatus({
                                        dialogStatus: true,
                                        contactNote: {
                                            contactNoteId: null, isNew: true, idx: contact.contactNotes.length
                                        },
                                    })
                                );
                            }}
                        >
                            Create
                        </TextButton>
                        {selectedContactNotes.length > 0 && (
                            <TextButton
                                onClick={() => {
                                    dispatch(
                                        onChangeConfirmDialogStatus({
                                            dialogOpen: true,
                                            contactNoteIds: selectedContactNotes,
                                        })
                                    );
                                }}
                            >
                                Delete Selected ({selectedContactNotes.length})
                            </TextButton>
                        )}
                    </Fragment>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <DataTableHeaderCheckboxCell onChange={(e) => onToggleAllSelected(e.target.checked)} />
                        <React.Fragment>
                            {columns.map((column, index) => {
                                return (
                                    <DataTableColumnHeaderCell key={index}>{column.title}</DataTableColumnHeaderCell>
                                );
                            })}
                        </React.Fragment>
                    </DataTableHeaderRow>
                    <DataTableBody>
                        {contact.contactNotes.map((row: ContactNote, index: number) => {
                            if (index < startRow || index >= startRow + rowsPerPage) {
                                return <React.Fragment key={row.contactNoteId}></React.Fragment>;
                            }
                            return renderRow(row, index);
                        })}
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={(startRow) => dispatch(onChangeContactNotePage(startRow))}
                    onChangeRowsPerPage={(rowsPerPage) => dispatch(onChangeContactNoteRowPerPage(rowsPerPage))}
                />
            </DataTable>

            {/* Add/Edit dialog */}
            <Dialog
                open={dialogStatus}
                className="contact-note-form"
                title="New contact note"
                dismissLabel="Cancel"
                confirmLabel="OK"
                enableConfirm={isDirty && isValid}
                onInteraction={(e) => {
                    if (e.detail.action === "confirm") {
                        dispatch(onAddOrUpdateContactNote(values));
                        dispatch(
                            onChangeDialogStatus({
                                dialogStatus: false,
                            })
                        );
                    } else
                        dispatch(
                            onChangeDialogStatus({
                                dialogStatus: false,
                            })
                        );
                }}
            >
                <form
                    autoComplete="off"
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}
                >
                    <GridRow className="contact-note-form-control">
                        <GridCell span={12}>
                            <Controller
                                name="noteTitle"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "The title is required",
                                    },
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        id="noteTitle"
                                        autoFocus
                                        maxLength={50}
                                        outlined
                                        label="Title"
                                        name="noteTitle"
                                        helperText={errors.noteTitle?.message ?? ""}
                                        invalid={!!errors.noteTitle?.message}
                                        value={value ?? ""}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </GridCell>
                        <GridCell span={12}>
                            <Controller
                                name="noteContent"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "The note content is required",
                                    },
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        id="noteContent"
                                        autoFocus
                                        outlined
                                        textarea
                                        label="Note Content"
                                        name="noteContent"
                                        helperText={errors.noteContent?.message ?? ""}
                                        invalid={!!errors.noteContent?.message}
                                        value={value ?? ""}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </GridCell>
                        <>
                            {values.extendedProperties?.map((property: Property, index: number) => {
                                return (
                                    <GridCell span={12} key={index}>
                                        <div>
                                            <ExtendedProperty
                                                index={index}
                                                className="ContactNote"
                                                property={property}
                                                clearErrors={clearErrors}
                                                control={control}
                                                errors={errors}
                                                setError={setError}
                                                setValue={setValue}
                                                trigger={trigger}
                                                key={index}
                                            />
                                        </div>
                                    </GridCell>
                                );
                            })}
                        </>
                    </GridRow>
                </form>
            </Dialog>

            {/* Delete dialog */}
            <Dialog
                open={confirmDialogStatus.dialogOpen}
                title="Delete"
                dismissLabel="Cancel"
                confirmLabel="Delete"
                enableConfirm
                onInteraction={async (e) => {
                    if (e.detail.action === "confirm") {
                        await dispatch(
                            onDeleteContactNote({
                                contactNoteIds: confirmDialogStatus.contactNoteIds,
                            })
                        );
                        await dispatch(deselectAllContactNotes());
                        dispatch(onChangeConfirmDialogStatus(false));
                    } else dispatch(onChangeConfirmDialogStatus(false));
                }}
            >
                <Typography use="body2">Are you sure to delete?</Typography>
            </Dialog>
        </Fragment>
    );
};

export default ContactNoteManager;
